import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiMapPin, FiClock, FiPackage, FiCheck, FiTruck, FiCopy, FiMap, FiWifi, FiWifiOff, FiShield } from 'react-icons/fi';
import api from '@/services/api';
import { formatCurrency, formatDate } from '@/utils/formatters';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import LeafletMap from '@/components/common/LeafletMap';
import wsService from '@/services/websocket';
import { useAuth } from '@/contexts/AuthContext';
import ChatButton from '@/components/chat/ChatButton';
import ClientHandshakeQR from '@/components/handshake/ClientHandshakeQR';

const OrderTracking = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [order, setOrder] = useState(null);
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [showMap, setShowMap] = useState(true);
  const [driverLocation, setDriverLocation] = useState(null);
  const [isWsConnected, setIsWsConnected] = useState(false);
  const [eta, setEta] = useState(null);

  const loadOrder = useCallback(async () => {
    try {
      const response = await api.get(`/orders/${id}`);
      setOrder(response.data);
      
      const storeResponse = await api.get(`/stores/${response.data.store_id}`);
      setStore(storeResponse.data);
      
      // Load driver location if there's a driver
      if (response.data.delivery_person_id) {
        try {
          const driverRes = await api.get(`/driver/location/${response.data.delivery_person_id}`);
          if (driverRes.data.location) {
            setDriverLocation(driverRes.data.location);
          }
        } catch (e) {
          console.log('Driver location not available');
        }
      }
      
      // Load ETA for orders in transit
      if (['coleta', 'em_rota', 'preparando', 'pendente'].includes(response.data.status)) {
        try {
          const etaRes = await api.get(`/orders/${id}/eta`);
          setEta(etaRes.data);
        } catch (e) {
          console.log('ETA not available');
        }
      }
    } catch (error) {
      console.error('Error loading order:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Setup WebSocket for real-time updates
  useEffect(() => {
    if (!token || !id) return;
    
    // Connect WebSocket
    wsService.connect(token);
    
    // Check connection status
    const checkConnection = setInterval(() => {
      setIsWsConnected(wsService.ws?.readyState === WebSocket.OPEN);
    }, 1000);
    
    // Subscribe to order updates
    const subscribeTimeout = setTimeout(() => {
      wsService.subscribeToOrder(id);
    }, 1000);
    
    // Listen for driver location updates
    const handleDriverLocation = (payload) => {
      if (payload.order_id === id) {
        setDriverLocation(payload.location);
      }
    };
    
    // Listen for order status updates
    const handleOrderUpdate = (payload) => {
      if (payload.order_id === id) {
        loadOrder(); // Reload order on status change
      }
    };
    
    wsService.addListener('driver_location', handleDriverLocation);
    wsService.addListener('order_update', handleOrderUpdate);
    
    return () => {
      clearInterval(checkConnection);
      clearTimeout(subscribeTimeout);
      wsService.removeListener('driver_location', handleDriverLocation);
      wsService.removeListener('order_update', handleOrderUpdate);
      wsService.unsubscribeFromOrder(id);
    };
  }, [token, id, loadOrder]);

  // Initial load and polling fallback
  useEffect(() => {
    loadOrder();
    // Polling as fallback (every 15s if WS not connected)
    const interval = setInterval(() => {
      if (!isWsConnected) {
        loadOrder();
      }
    }, 15000);
    return () => clearInterval(interval);
  }, [loadOrder, isWsConnected]);

  const copySecurityCode = () => {
    if (order?.security_code) {
      navigator.clipboard.writeText(order.security_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Pedido não encontrado</p>
      </div>
    );
  }

  const statusSteps = [
    { key: 'pendente', label: 'Pendente', icon: FiClock, description: 'Aguardando confirmação da loja' },
    { key: 'preparando', label: 'Preparando', icon: FiPackage, description: 'A loja está preparando seu pedido' },
    { key: 'coleta', label: 'Aguardando Coleta', icon: FiTruck, description: 'Pronto! Aguardando entregador' },
    { key: 'em_rota', label: 'Em Rota', icon: FiTruck, description: 'Seu pedido está a caminho' },
    { key: 'entregue', label: 'Entregue', icon: FiCheck, description: 'Pedido entregue com sucesso!' }
  ];

  const currentStepIndex = statusSteps.findIndex(s => s.key === order.status);
  const currentStep = statusSteps[currentStepIndex];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-blue-600 to-blue-800 text-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/cliente/pedidos')}
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
          >
            <FiArrowLeft className="text-xl" />
          </button>
          <div className="flex-1">
            <h1 className="font-semibold">Pedido #{order.id.slice(0, 8)}</h1>
            <p className="text-sm text-blue-100">{formatDate(order.created_at)}</p>
          </div>
          {/* Connection Status */}
          <ChatButton orderId={id} className="mr-2" />
          <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
            isWsConnected ? 'bg-green-500/20 text-green-100' : 'bg-yellow-500/20 text-yellow-100'
          }`}>
            {isWsConnected ? <FiWifi className="text-sm" /> : <FiWifiOff className="text-sm" />}
            {isWsConnected ? 'Ao vivo' : 'Reconectando...'}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-4">
        {/* ETA Card - Show prominently when in transit */}
        {eta && eta.eta_text && order.status !== 'entregue' && (
          <div className={`rounded-xl p-5 ${
            order.status === 'em_rota' 
              ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white' 
              : 'bg-gradient-to-r from-blue-500 to-blue-600 text-white'
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-90 mb-1">Tempo estimado de chegada</p>
                <h2 className="text-3xl font-bold">{eta.eta_text}</h2>
                {eta.distance_km && (
                  <p className="text-sm opacity-80 mt-1">
                    📍 {eta.distance_km} km de distância
                  </p>
                )}
              </div>
              <div className="text-right">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                  order.status === 'em_rota' ? 'bg-white/20' : 'bg-white/20'
                }`}>
                  {order.status === 'em_rota' ? (
                    <FiTruck className="text-3xl animate-bounce" />
                  ) : (
                    <FiClock className="text-3xl" />
                  )}
                </div>
              </div>
            </div>
            {order.status === 'em_rota' && (
              <div className="mt-3 flex items-center gap-2 text-sm bg-white/10 rounded-lg px-3 py-2">
                <span className="animate-pulse">🛵</span>
                <span>Entregador a caminho do seu endereço</span>
              </div>
            )}
          </div>
        )}

        {/* Current Status Card */}
        <div className={`rounded-xl p-5 ${
          order.status === 'entregue' ? 'bg-green-50 border-2 border-green-300' :
          order.status === 'em_rota' ? 'bg-orange-50 border-2 border-orange-300' :
          'bg-blue-50 border-2 border-blue-300'
        }`}>
          <div className="flex items-center gap-3 mb-2">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
              order.status === 'entregue' ? 'bg-green-500' :
              order.status === 'em_rota' ? 'bg-orange-500' :
              'bg-blue-500'
            } text-white`}>
              <currentStep.icon className="text-xl" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800">{currentStep.label}</h2>
              <p className="text-sm text-gray-600">{currentStep.description}</p>
            </div>
          </div>
          {order.status !== 'entregue' && (
            <p className="text-xs text-gray-500 mt-3">
              {isWsConnected ? '🟢 Atualizações em tempo real ativas' : '🟡 Atualizando a cada 15 segundos'}
            </p>
          )}
        </div>

        {/* HANDSHAKE DIGITAL - QR Code de Segurança */}
        {(order.status === 'em_rota' || order.status === 'coleta') && (
          <ClientHandshakeQR orderId={id} orderStatus={order.status} />
        )}

        {/* Security Code - Código antigo (fallback) */}
        {order.status !== 'entregue' && order.security_code && (
          <div className="bg-yellow-50 border-2 border-yellow-300 rounded-xl p-5">
            <h3 className="font-semibold text-yellow-800 mb-2 flex items-center gap-2">
              🔐 Código de Segurança (Alternativo)
            </h3>
            <div className="flex items-center gap-3">
              <p className="text-4xl font-bold text-yellow-700 tracking-[0.3em] font-mono">{order.security_code}</p>
              <button 
                onClick={copySecurityCode}
                className={`p-2 rounded-lg transition-colors ${copied ? 'bg-green-100 text-green-600' : 'bg-yellow-100 text-yellow-600 hover:bg-yellow-200'}`}
              >
                <FiCopy />
              </button>
            </div>
            <p className="text-sm text-yellow-600 mt-3">
              {copied ? '✓ Copiado!' : 'Use este código se o QR Code não funcionar'}
            </p>
          </div>
        )}

        {/* Delivery Person Info */}
        {order.delivery_person_name && (
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <h3 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
              <FiTruck className="text-blue-600" /> Entregador
            </h3>
            <p className="font-medium text-lg">{order.delivery_person_name}</p>
            <p className="text-sm text-gray-500">Seu pedido está com este entregador</p>
          </div>
        )}

        {/* Live Map */}
        {(order.status === 'em_rota' || order.status === 'coleta') && store && (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <button
              onClick={() => setShowMap(!showMap)}
              className="w-full p-3 flex items-center justify-between text-gray-700 hover:bg-gray-50"
            >
              <span className="flex items-center gap-2 font-medium">
                <FiMap className="text-blue-600" /> Acompanhar no Mapa
              </span>
              <span className="text-sm text-gray-500">{showMap ? 'Ocultar' : 'Mostrar'}</span>
            </button>
            {showMap && (
              <LeafletMap
                storeLocation={store.coordinates}
                clientLocation={order.delivery_coordinates}
                driverLocation={driverLocation}
                showRoute={true}
                height="300px"
              />
            )}
          </div>
        )}

        {/* Status Timeline */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <h3 className="font-semibold text-gray-800 mb-4">Progresso do Pedido</h3>
          <div className="relative">
            {statusSteps.map((step, index) => {
              const isCompleted = index <= currentStepIndex;
              const isCurrent = index === currentStepIndex;
              const Icon = step.icon;
              
              return (
                <div key={step.key} className="flex items-start gap-3 pb-4 last:pb-0">
                  <div className="relative">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      isCompleted ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-400'
                    } ${isCurrent ? 'ring-4 ring-blue-100' : ''}`}>
                      <Icon className="text-lg" />
                    </div>
                    {index < statusSteps.length - 1 && (
                      <div className={`absolute left-1/2 top-10 w-0.5 h-6 -translate-x-1/2 ${
                        isCompleted && index < currentStepIndex ? 'bg-blue-600' : 'bg-gray-200'
                      }`} />
                    )}
                  </div>
                  <div className="pt-2">
                    <p className={`font-medium ${isCompleted ? 'text-gray-800' : 'text-gray-400'}`}>
                      {step.label}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Order Details */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <h3 className="font-semibold text-gray-800 mb-3">Entrega em</h3>
          <div className="flex items-start gap-2">
            <FiMapPin className="text-blue-600 mt-1" />
            <p className="text-gray-800">{order.delivery_address}</p>
          </div>
        </div>

        {/* Store Info */}
        {store && (
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <h3 className="font-semibold text-gray-800 mb-3">Loja</h3>
            <p className="font-medium">{store.name}</p>
            <p className="text-sm text-gray-600">{store.address}</p>
          </div>
        )}

        {/* Items */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <h3 className="font-semibold text-gray-800 mb-3">Itens do Pedido</h3>
          <div className="space-y-2">
            {order.items.map((item, index) => (
              <div key={index} className="flex justify-between text-sm">
                <span className="text-gray-600">{item.quantity}x {item.product_name}</span>
                <span className="text-gray-800">{formatCurrency(item.subtotal)}</span>
              </div>
            ))}
            <div className="border-t pt-2 mt-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Taxa de Entrega</span>
                <span className="text-gray-800">{formatCurrency(order.delivery_fee)}</span>
              </div>
              <div className="flex justify-between font-semibold mt-2 text-lg">
                <span>Total</span>
                <span className="text-blue-600">{formatCurrency(order.total_value)}</span>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default OrderTracking;
